import React, { useState, useEffect, useRef } from 'react';
import { RoomInfo, HeroId, ClassId, LobbyChatMessage, RoomListEntry } from '../types';
import { getAsset } from '../services/assets';
import { HERO_DATA as CENTRAL_HERO_DATA, CLASS_DATA as CENTRAL_CLASS_DATA, getHeroDescription, getClassDescription } from '../data/heroData';
import LobbyChat from './LobbyChat';
import { isTouchDevice } from '../services/device';
import { Language, t } from '../data/translations';
import { useTouchHint } from '../hooks/useTouchHint';
import RoomList from './RoomList';
import ProfileScreen from './ProfileScreen';
import { useShellLayout } from '../hooks/useShellLayout';
import tavernBg from '../assets/ui/fantasy/tavern_bg.png';
import woodPanel from '../assets/ui/fantasy/wood_panel.png';
import parchmentTexture from '../assets/ui/fantasy/parchment_texture.png';
import btnWood from '../assets/ui/fantasy/btn_wood.png';

interface RoomLobbyProps {
  room: RoomInfo;
  localPlayerId: string;
  onLeave: () => void;
  onToggleReady: () => void;
  onStart: () => void;
  onSelectSetup: (hero: HeroId, playerClass: ClassId) => void;
  availableHeroes: HeroId[];
  chatMessages: LobbyChatMessage[];
  onSendChat: (text: string) => void;
  language: Language;
  availableRooms: RoomListEntry[];
  onSwitchRoom: (room: RoomListEntry) => void;
  onRefreshRooms?: () => void;
}

// Use centralized class data
const CLASS_DATA = CENTRAL_CLASS_DATA;

const RoomLobby: React.FC<RoomLobbyProps> = ({
  room,
  localPlayerId,
  onLeave,
  onToggleReady,
  onStart,
  onSelectSetup,
  availableHeroes,
  chatMessages,
  onSendChat,
  language,
  availableRooms,
  onSwitchRoom,
  onRefreshRooms
}) => {
  const isHost = localPlayerId === room.hostId;
  const localPlayer = room.players.find(p => p.id === localPlayerId) as (RoomInfo['players'][number] & { hero?: HeroId; playerClass?: ClassId; hasSelected?: boolean }) | undefined;

  // Substitute EROK_BURU with VERG for player selection
  const displayHeroes = availableHeroes.map(h => h === 'EROK_BURU' ? 'VERG' as HeroId : h);

  const [selectedHero, setSelectedHero] = useState<HeroId | null>(localPlayer?.hero || null);
  const [selectedClass, setSelectedClass] = useState<ClassId | null>(localPlayer?.playerClass || null);
  const [hoveredItem, setHoveredItem] = useState<{ type: 'hero' | 'class', data: any } | null>(null);
  const [hintTrigger, setHintTrigger] = useState<'hover' | 'longpress' | null>(null);
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  const { isMobileShell } = useShellLayout();
  const [activeMobilePanel, setActiveMobilePanel] = useState<'chat' | 'rooms'>('rooms');
  const [selectedProfile, setSelectedProfile] = useState<{
    id: string
    name: string
    fame: number
    hero?: HeroId
    playerClass?: ClassId
  } | null>(null);

  // Auto-refresh rooms on mount
  useEffect(() => {
    if (onRefreshRooms) onRefreshRooms();
  }, []);

  useEffect(() => {
    if (selectedHero && selectedClass) {
      onSelectSetup(selectedHero, selectedClass);
    }
  }, [selectedHero, selectedClass]);

  const handleMouseEnter = (type: 'hero' | 'class', data: any) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoveredItem({ type, data });
      setHintTrigger('hover');
    }, 500);
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoveredItem(null);
  };

  const canStart = isHost && room.players.length === 2 && room.players.every(p => p.isReady);
  const canToggleReady = !!selectedHero && !!selectedClass && !!localPlayer?.hasSelected;

  // Helper component for touch-aware buttons
  const TouchAwareButton: React.FC<{
    onTap: () => void
    onLongPress: () => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    disabled?: boolean
    className?: string
    children: React.ReactNode
  }> = ({ onTap, onLongPress, onMouseEnter, onMouseLeave, disabled, className, children }) => {
    const touch = useTouchHint({ onLongPress })
    return (
      <button
        {...touch}
        onClick={(e) => {
          if (touch.interceptClick(e)) return
          onTap()
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        className={className}
      >
        {children}
      </button>
    )
  }

  const PlayerSlot: React.FC<{
    player: { id: string, name: string, isReady: boolean, hasSelected?: boolean, fame?: number, hero?: HeroId, playerClass?: ClassId } | undefined
    isHostSlot: boolean
    onProfileClick?: (player: { id: string, name: string, isReady: boolean, hasSelected?: boolean, fame?: number, hero?: HeroId, playerClass?: ClassId }) => void
  }> = ({ player, onProfileClick }) => (
    <div
      className={`flex items-center justify-between bg-slate-800/60 p-2 rounded-lg border border-slate-700 ${player ? 'cursor-pointer hover:bg-slate-700/60 transition-colors' : ''}`}
      onClick={() => player && onProfileClick?.(player)}
    >
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${player ? (player.isReady ? 'bg-green-400 animate-pulse' : 'bg-yellow-400') : 'bg-slate-600'}`}></div>
        <div className="flex flex-col min-w-0">
          <span className={`text-sm truncate ${player ? 'text-white' : 'text-slate-400'}`}>
            {player ? player.name : t('ui.waitingForPlayer', language)}
            {player?.id === room.hostId && <span className="text-xs text-amber-400 ml-1">({t('ui.host', language)})</span>}
          </span>
          {player && <span className="text-xs text-cyan-400">⭐ {player.fame ?? 0}</span>}
          {player && (
            <span className="text-[10px] text-slate-500 truncate">
              {player.hasSelected ? t('ui.setupSelected', language) : t('ui.choosing', language)}
            </span>
          )}
        </div>
      </div>
      <span className={`text-sm font-bold flex-shrink-0 ${player ? (player.isReady ? 'text-green-400' : 'text-yellow-400') : 'text-slate-500'}`}>
        {player ? (player.isReady ? '✓' : '✗') : '-'}
      </span>
    </div>
  );

  const commonButtonStyles: React.CSSProperties = {
    borderStyle: 'solid',
    borderWidth: '10px',
    borderImageSource: `url(${btnWood})`,
    borderImageSlice: '30 fill',
    textShadow: '1px 1px 2px black',
    fontFamily: 'Cinzel'
  };

  return (
    <div 
      className="w-full h-screen flex flex-col items-center justify-center animate-[fadeIn_0.5s_ease-out] overflow-hidden bg-cover bg-center font-['Crimson_Text']" 
      style={{ backgroundImage: `url(${tavernBg})` }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      <div
        className={`relative z-10 w-full max-w-[95rem] p-6 rounded-xl border-[6px] border-[#3E2723] outline outline-2 outline-[#DAA520] shadow-2xl transition-transform duration-500 ease-in-out h-[88vh]
          ${isMobileShell ? 'flex gap-2' : 'grid grid-cols-12 gap-6'}
        `}
        style={{
          backgroundImage: `url(${woodPanel})`,
          backgroundSize: 'cover',
          ...(isMobileShell ? {
            width: '135%',
            transform: activeMobilePanel === 'chat' ? 'translateX(10%)' : 'translateX(-10%)'
          } : {})
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40 rounded-lg pointer-events-none"></div>
        {/* Column 1: Chat (Left) */}
        <div className={`${isMobileShell ? 'w-1/4' : 'col-span-3'} h-full min-w-0 transition-opacity duration-300 ${isMobileShell && activeMobilePanel !== 'chat' ? 'opacity-50 cursor-pointer' : ''}`}
             onClick={() => isMobileShell && setActiveMobilePanel('chat')}>
          <LobbyChat
            messages={chatMessages}
            onSend={onSendChat}
            localPlayerId={localPlayerId}
            language={language}
          />
        </div>

        {/* Column 2: Players & Controls (Middle-Left) */}
        <div className={`${isMobileShell ? 'w-1/4' : 'col-span-3'} h-full flex flex-col gap-4 min-w-0`} onClick={() => isMobileShell && setActiveMobilePanel('chat')}>
          <div className="text-center p-2 bg-slate-800/40 rounded border border-slate-700/50">
            <h2 className="text-xl font-bold text-white truncate">{t('ui.lobby', language)}</h2>
            <div className="flex items-center justify-center gap-1 mt-1">
              <span className="text-sm font-medium text-cyan-400 tracking-wide truncate">{room.roomName}</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <PlayerSlot
              player={room.players.find(p => p.id === room.hostId)}
              isHostSlot={true}
              onProfileClick={(p) => setSelectedProfile({ id: p.id, name: p.name, fame: p.fame ?? 0, hero: p.hero, playerClass: p.playerClass })}
            />
            <PlayerSlot
              player={room.players.find(p => p.id !== room.hostId)}
              isHostSlot={false}
              onProfileClick={(p) => setSelectedProfile({ id: p.id, name: p.name, fame: p.fame ?? 0, hero: p.hero, playerClass: p.playerClass })}
            />
          </div>

          <div className="mt-auto flex flex-col gap-3 relative z-10">
            <button
              onClick={onToggleReady}
              disabled={!canToggleReady}
              className={`w-full font-bold text-base py-2 px-4 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:grayscale ${localPlayer?.isReady
                ? 'text-yellow-100 hover:brightness-110'
                : 'text-amber-100 hover:brightness-110'
                }`}
              style={commonButtonStyles}
            >
              {localPlayer?.isReady ? t('ui.setNotReady', language) : (!canToggleReady ? t('ui.selectHeroAndClass', language) : t('ui.readyUp', language))}
            </button>
            {isHost && (
              <button
                onClick={onStart}
                disabled={!canStart}
                className="w-full text-amber-100 font-bold text-base py-2 px-4 transition-all duration-300 shadow-lg hover:brightness-110 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
                style={commonButtonStyles}
              >
                {t('ui.startGame', language)}
              </button>
            )}
            <button
              onClick={onLeave}
              className="w-full text-red-200 font-bold text-base py-1 px-4 transition-all duration-300 hover:brightness-110 opacity-80 hover:opacity-100"
              style={commonButtonStyles}
            >
              {t('ui.leave', language)}
            </button>
          </div>
        </div>

        {/* Column 3: Setup (Middle-Right) */}
        <div className={`${isMobileShell ? 'w-1/4' : 'col-span-3'} h-full bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 flex flex-col relative min-w-0`} onMouseLeave={handleMouseLeave} onClick={() => isMobileShell && setActiveMobilePanel('rooms')}>
          <h3 className="text-lg font-bold text-white mb-4 border-b border-slate-700 pb-1 truncate">{t('ui.setupYourChampion', language)}</h3>

          {/* Hero Selection */}
          <div className="mb-4">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-bold">{t('ui.chooseHero', language)}</div>
            <div className="grid grid-cols-3 gap-2">
              {displayHeroes.map(h => {
                const heroInfo = CENTRAL_HERO_DATA[h];
                const data = heroInfo ? {
                  name: t(`hero.${h}.name`, language),
                  desc: t(`hero.${h}.description`, language), // fallback or just use helper
                  avatar: heroInfo.avatar,
                  card: heroInfo.card
                } : { name: h, desc: 'Unknown Hero', avatar: '', card: '' };
                const isSelected = selectedHero === h;
                return (
                  <TouchAwareButton
                    key={h}
                    onTap={() => { if (!localPlayer?.isReady) setSelectedHero(h); setHoveredItem(null) }}
                    onLongPress={() => { setHoveredItem({ type: 'hero', data: { ...data, id: h, desc: getHeroDescription(h, language) } }); setHintTrigger('longpress'); }}
                    disabled={!!localPlayer?.isReady}
                    onMouseEnter={() => handleMouseEnter('hero', { ...data, id: h, desc: getHeroDescription(h, language) })}
                    onMouseLeave={handleMouseLeave}
                    className={`relative group transition-all duration-300 aspect-square rounded-lg overflow-hidden border-2 ${isSelected ? 'border-indigo-500 shadow-indigo-500/50 z-10 scale-105' : 'border-slate-600 opacity-80 hover:opacity-100'}`}
                  >
                    <img src={data.avatar} className="w-full h-full object-cover" />
                    {isSelected && <div className="absolute top-0 right-0 w-3 h-3 bg-indigo-500 rounded-bl flex items-center justify-center text-[8px] text-white">✓</div>}
                  </TouchAwareButton>
                )
              })}
            </div>
          </div>

          {/* Class Selection */}
          <div className="mb-4">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-bold">{t('ui.chooseClass', language)}</div>
            <div className="flex justify-around gap-2">
              {(['WARRIOR', 'MAGE', 'OCCULTIST'] as ClassId[]).map(c => {
                const classInfo = CLASS_DATA[c];
                const data = {
                  id: c,
                  name: t(`class.${c}.name`, language),
                  desc: t(`class.${c}.desc`, language),
                  icon: classInfo.icon
                };
                const isSelected = selectedClass === c;
                return (
                  <TouchAwareButton
                    key={c}
                    onTap={() => { if (!localPlayer?.isReady) setSelectedClass(c); setHoveredItem(null) }}
                    onLongPress={() => { setHoveredItem({ type: 'class', data }); setHintTrigger('longpress'); }}
                    disabled={!!localPlayer?.isReady}
                    onMouseEnter={() => handleMouseEnter('class', data)}
                    onMouseLeave={handleMouseLeave}
                    className={`relative group transition-all duration-300 p-2 rounded-lg border-2 w-20 h-20 flex items-center justify-center ${isSelected ? 'bg-purple-500/20 border-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.3)]' : 'bg-slate-800 border-slate-700 hover:border-slate-500'}`}
                  >
                    <img src={data.icon} className="w-full h-full object-contain" />
                    {isSelected && <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center text-[8px] text-white font-bold">✓</div>}
                  </TouchAwareButton>
                )
              })}
            </div>
          </div>

          <div className="mt-auto p-2 bg-black/20 rounded text-xs text-slate-400 italic text-center">
            {!selectedHero || !selectedClass ? t('ui.selectHeroAndClassPrompt', language) : t('ui.readyToLockIn', language)}
          </div>

        </div>

        {/* Column 4: Room List (Right) / Preview Panel */}
        <div className={`${isMobileShell ? 'w-1/4' : 'col-span-3'} h-full bg-slate-900/50 rounded-lg border border-slate-700/50 flex flex-col min-w-0 relative transition-opacity duration-300 ${isMobileShell && activeMobilePanel !== 'rooms' ? 'opacity-50 cursor-pointer' : ''}`} onClick={() => isMobileShell && setActiveMobilePanel('rooms')}>
          {hoveredItem ? (
            <div
              className={`absolute inset-0 z-50 flex flex-col items-center p-4 animate-[fadeIn_0.5s_ease-out] bg-slate-900/95 backdrop-blur-md rounded-lg ${hintTrigger === 'longpress' ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
              onClick={() => hintTrigger === 'longpress' && setHoveredItem(null)}
            >
              <div className="flex flex-col items-center gap-4 w-full h-full">
                {hoveredItem.type === 'hero' ? (
                  <img src={hoveredItem.data.card} className="w-full max-h-[60%] object-contain rounded-lg shadow-2xl border border-slate-500" />
                ) : (
                  <div className="w-32 h-32 p-4 bg-slate-800 rounded-full border-2 border-slate-500 shadow-2xl flex-shrink-0 mt-4">
                    <img src={hoveredItem.data.icon} className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="text-center overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent pb-4 w-full px-2">
                  <h3 className="text-xl font-bold text-white mb-2">{hoveredItem.data.name}</h3>
                  <p className="text-slate-200 text-sm leading-relaxed">{hoveredItem.data.desc}</p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="p-3 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{t('multiplayer.availableRooms', language)}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onRefreshRooms && onRefreshRooms()}
                className="text-[10px] bg-slate-800 hover:bg-slate-700 text-cyan-400 px-2 py-1 rounded border border-slate-600 transition-colors"
              >
                ↻ Refresh
              </button>
              <div className="text-[10px] text-slate-500 animate-pulse">● Live</div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            <RoomList
              rooms={availableRooms}
              onJoin={onSwitchRoom}
              currentRoomCode={room.code}
              language={language}
              emptyMessage={t('multiplayer.noRooms', language)}
            />
          </div>
        </div>

      </div>
      {selectedProfile && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl">
            <ProfileScreen
              gameState={null}
              onBack={() => setSelectedProfile(null)}
              language={language}
              isModal={true}
              viewOnly={selectedProfile.id !== localPlayerId}
              playerData={{
                name: selectedProfile.name,
                fame: selectedProfile.fame,
                hero: selectedProfile.hero,
                playerClass: selectedProfile.playerClass
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomLobby;
